import _ from 'lodash';
import { toast } from '@/composables/useToast';

/**
 * HTTP status codes that are considered non-recoverable errors
 * These will always show toast notifications regardless of hideToast setting
 */
export const NON_RECOVERABLE_STATUSES = [401, 403, 500];

/**
 * HTTP status codes that should show warning toasts instead of error toasts
 * These represent conflicts, resource states, or other recoverable issues
 */
export const WARNING_STATUSES = [409, 422, 423, 429];

/**
 * Toast configuration defaults
 * Centralized toast timing and behavior settings
 */
export const TOAST_CONFIG = {
    error: {
        autoClose: 10000,
        defaultMessage: 'An error occurred'
    },
    warning: {
        autoClose: 8000,
        defaultMessage: 'A warning occurred'
    },
    success: {
        autoClose: 5000
    },
    nonRecoverableStatuses: NON_RECOVERABLE_STATUSES,
    warningStatuses: WARNING_STATUSES
};

/**
 * Extracts error messages from various response formats
 * Handles both string responses and complex nested error objects
 *
 * @param {Object|String} response - The response object or error message
 * @param {Object} response.data - Response data containing potential errors
 * @param {String} response.data.error - Direct error message
 * @param {Array|Object} response.data.detail - Detailed error information
 * @param {Object} response.error - Error object with message
 * @param {String} response.error.message - Error message
 * @param {Array<string>} excludeKeys - Optional array of keys to exclude from error search
 *
 * @returns {String|null} - Extracted error message or null if no error found
 *
 * @example
 * // Simple error object
 * extractErrorMessage({ data: { error: 'Something went wrong' } })
 * // Returns: 'Something went wrong'
 *
 * @example
 * // Array of detailed errors
 * extractErrorMessage({
 *   data: {
 *     detail: [
 *       { msg: 'Field is required' },
 *       { message: 'Invalid format' }
 *     ]
 *   }
 * })
 * // Returns: 'Field is required, Invalid format'
 *
 * @example
 * // Exclude specific keys (useful in success interceptors)
 * extractErrorMessage(response, ['data.message', 'data.detail'])
 * // Will not search in data.message or data.detail
 */
export function extractErrorMessage(response, excludeKeys = []) {
    // Handle string responses directly
    if (_.isString(response)) {
        return response;
    }

    // Extract potential error sources (skip excluded keys)
    const errorData = excludeKeys.includes('data.error') ? null : _.get(response, 'data.error', null);
    const errorMessage = excludeKeys.includes('error.message') ? null : _.get(response, 'error.message', null);
    const dataMessage = excludeKeys.includes('data.message') ? null : _.get(response, 'data.message', null);
    let detailErrors = excludeKeys.includes('data.detail') ? null : _.get(response, 'data.detail', null);

    // Process detailed errors (can be array or object)
    if (detailErrors) {
        if (Array.isArray(detailErrors)) {
            // Handle array of error details
            detailErrors = detailErrors
                .map((detail) => {
                    // Extract message from various possible fields
                    return detail.msg || detail.message || detail.error || (detail ? String(detail) : null);
                })
                .filter(Boolean) // Remove null/empty values
                .join(', ');
        } else {
            // Handle single error detail object
            detailErrors = detailErrors.msg || detailErrors.message || detailErrors.error || (detailErrors ? String(detailErrors) : null);
        }
    }

    // Return first available error message
    const finalMessage = errorData || errorMessage || dataMessage || detailErrors || null;
    return finalMessage;
}

/**
 * Checks if an HTTP status code is considered non-recoverable
 * These errors typically require user intervention and should always show toasts
 *
 * @param {number} status - HTTP status code
 * @param {number[]} nonRecoverableStatuses - Array of status codes to check against
 * @returns {boolean} - Whether the status is non-recoverable
 */
export function isNonRecoverableError(status, nonRecoverableStatuses = NON_RECOVERABLE_STATUSES) {
    return nonRecoverableStatuses.includes(status);
}

/**
 * Checks if an HTTP status code should show a warning toast instead of an error toast
 * These statuses typically represent conflicts, resource states, or other recoverable issues
 *
 * @param {number} status - HTTP status code
 * @param {number[]} warningStatuses - Array of status codes to check against
 * @returns {boolean} - Whether the status should show a warning toast
 */
export function isWarningStatus(status, warningStatuses = WARNING_STATUSES) {
    return warningStatuses.includes(status);
}

/**
 * Determines if toast notifications should be hidden based on configuration and error type
 *
 * @param {boolean} hideToast - Configuration to hide toast
 * @param {number} status - HTTP status code
 * @param {number[]} nonRecoverableStatuses - Array of non-recoverable status codes
 * @returns {boolean} - Whether to hide the toast
 */
export function shouldHideToast(hideToast, status, nonRecoverableStatuses = NON_RECOVERABLE_STATUSES) {
    // Never hide toasts for non-recoverable errors
    if (isNonRecoverableError(status, nonRecoverableStatuses)) {
        return false;
    }

    return !!hideToast;
}

/**
 * Handles error toast notifications with automatic warning detection
 * Automatically shows warning toasts for conflict/recoverable statuses and error toasts for others
 * Respects hideToast configuration while ensuring critical errors are always shown
 *
 * @param {string} message - Error message to display
 * @param {boolean} hideToast - Whether to suppress the toast
 * @param {number} status - HTTP status code
 * @param {Object} options - Additional toast options
 * @param {number} options.autoClose - Auto-close timeout in milliseconds
 *
 * @example
 * handleErrorToast('Invalid credentials', false, 401)
 * // Shows error toast regardless of hideToast setting
 *
 * @example
 * handleErrorToast('Resource conflict', false, 409)
 * // Shows warning toast for conflict status
 *
 * @example
 * handleErrorToast('Network timeout', true, 408)
 * // Respects hideToast for recoverable errors
 */
export function handleErrorToast(message, hideToast = false, status = null, options = {}) {
    if (!message) return;

    const shouldHide = shouldHideToast(hideToast, status, TOAST_CONFIG.nonRecoverableStatuses);
    if (shouldHide) return;

    // Determine if this should be a warning toast based on status code
    if (isWarningStatus(status, TOAST_CONFIG.warningStatuses)) {
        const toastOptions = {
            autoClose: options.autoClose ?? TOAST_CONFIG.warning.autoClose,
            ...options
        };
        toast.warning(message, toastOptions);
    } else {
        const toastOptions = {
            autoClose: options.autoClose ?? TOAST_CONFIG.error.autoClose,
            ...options
        };
        toast.error(message, toastOptions);
    }
}

/**
 * Handles warning toast notifications
 * Dedicated function for showing warning toasts with consistent behavior
 *
 * @param {string} message - Warning message to display
 * @param {boolean} hideToast - Whether to suppress the toast
 * @param {Object} options - Additional toast options
 * @param {number} options.autoClose - Auto-close timeout in milliseconds
 *
 * @example
 * handleWarningToast('Resource conflict detected', false)
 * // Shows: 'Resource conflict detected' as warning toast
 */
export function handleWarningToast(message, hideToast = false, options = {}) {
    if (!message || hideToast) return;

    const toastOptions = {
        autoClose: options.autoClose ?? TOAST_CONFIG.warning.autoClose,
        ...options
    };

    toast.warning(message, toastOptions);
}

/**
 * Handles success toast notifications
 * Capitalizes the first letter of the message for consistent formatting
 *
 * @param {string} message - Success message to display
 * @param {boolean} hideToast - Whether to suppress the toast
 * @param {Object} options - Additional toast options
 * @param {number} options.autoClose - Auto-close timeout in milliseconds
 *
 * @example
 * handleSuccessToast('user created successfully', false)
 * // Shows: 'User created successfully'
 */
export function handleSuccessToast(message, hideToast = false, options = {}) {
    if (!message || hideToast) return;

    const toastOptions = {
        autoClose: options.autoClose ?? TOAST_CONFIG.success.autoClose,
        ...options
    };

    toast.success(_.capitalize(message), toastOptions);
}
